export type NutritionRoute = {
  method: string;
  path: string;
  full_path: string;
  summary: string;
  description?: string;
  tags: string[];
  params?: { name: string; in: string; required?: boolean; type?: string; desc?: string }[];
  body?: string;
  request?: any;
  response?: any;
  example?: any;
  notes?: string;
  alias?: string;
  aliases?: string[];
  operationId?: string;
  docs_url?: string;
  /** SDK mode only: the mobile SDK method that wraps this route, from /sdk/capabilities. */
  sdk_method?: string | null;
  sdk_covered?: boolean;
  sdk_note?: string;
};

export function nutritionRouteKey(route: NutritionRoute): string {
  return route.operationId || `${route.method}:${route.path}`;
}

export function nutritionRouteAlias(route: NutritionRoute): string {
  if (route.alias) return route.alias;
  const last = (route.path || '').split('/').filter(Boolean).pop() || '';
  return last.split('{')[0] || nutritionRouteKey(route);
}

export function filterNutritionRoutes(routes: NutritionRoute[], q: string, tag = 'all'): NutritionRoute[] {
  const needle = q.trim().toLowerCase();
  return routes.filter((r) => {
    if (tag !== 'all' && !(r.tags || []).includes(tag)) return false;
    if (!needle) return true;
    const blob = [
      r.method,
      r.path,
      r.full_path,
      r.summary,
      r.description,
      r.body,
      r.alias,
      r.operationId,
      // So "searchForFoodSemantic" finds the route in SDK mode, where the method
      // name is what the user actually knows.
      r.sdk_method,
      ...(r.tags || []),
      ...(r.aliases || []),
    ]
      .join(' ')
      .toLowerCase();
    return blob.includes(needle);
  });
}

/** `@token` immediately before the caret, if the user is still typing a mention. */
export function mentionAtCaret(value: string, caret: number): { start: number; query: string } | null {
  const left = value.slice(0, caret);
  const m = left.match(/(^|[\s(])@([A-Za-z0-9_./-]*)$/);
  if (!m) return null;
  const query = m[2] || '';
  return { start: caret - query.length - 1, query };
}

export function insertNutritionMention(value: string, caret: number, alias: string): { next: string; caret: number } {
  const hit = mentionAtCaret(value, caret);
  const token = `@${alias} `;
  if (!hit) {
    const prefix = value.trim();
    const next = prefix ? `${prefix} ${token}` : token;
    return { next, caret: next.length };
  }
  const next = value.slice(0, hit.start) + token + value.slice(caret);
  return { next, caret: hit.start + token.length };
}

/** Composer @ token for a chain name (no spaces — mention parser is token-based). */
export function chainMentionSlug(name: string): string {
  const slug = (name || '').replace(/[^A-Za-z0-9]+/g, '');
  return slug.slice(0, 48) || 'chain';
}
